import { BarcodeDetectionResult } from './barcode-result'
import { IBarcodeDetector, IBarcodeDetectorOptions } from './barcode-detector'
import { StabilityTracker } from 'shared-libs/helpers/vision/stability-tracker'
import { polygonToAABB } from 'shared-libs/helpers/vision/polygon-utils'

const DETECTION_MAX_FPS = 15
const DETECTION_INTERVAL_MS = 1000 / DETECTION_MAX_FPS

export class NativeBarcodeDetector implements IBarcodeDetector {
  private detector?: BarcodeDetector
  private videoElement?: HTMLVideoElement
  private stabilityTracker: StabilityTracker
  private stream?: MediaStream
  private running = false

  constructor(private options?: IBarcodeDetectorOptions) {
    this.stabilityTracker = new StabilityTracker({
      steadinessTolerance: options?.steadinessTolerance,
      closenessThreshold: options?.closenessThreshold,
    })
  }

  async detect(
    videoElement: HTMLVideoElement,
    onResult: (results: BarcodeDetectionResult[]) => void,
    onError?: (error: unknown) => void
  ): Promise<void> {
    try {
      this.detector = new BarcodeDetector()
      this.videoElement = videoElement

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
        },
      })

      videoElement.srcObject = this.stream
      await videoElement.play()

      this.running = true

      this.start(onResult, onError)
    } catch (error) {
      onError?.(error)
    }
  }

  stop(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = undefined
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null
      this.videoElement = undefined
    }

    this.detector = undefined
    this.videoElement = undefined
    this.running = false
  }

  private async start(
    onResult: (results: BarcodeDetectionResult[]) => void,
    onError?: (error: unknown) => void
  ) {
    try {
      while (this.running) {
        const start = performance.now()
        const detector = this.detector
        const videoElement = this.videoElement
        if (!detector || !videoElement) {
          break
        }
        const barcodes = await detector.detect(videoElement)
        const end = performance.now()

        const activeKeys = new Set(barcodes.map((b) => b.rawValue))

        for (const barcode of barcodes) {
          barcode.cornerPoints = this.stabilityTracker.updateHistory(barcode.rawValue, barcode.cornerPoints)
        }

        this.stabilityTracker.pruneHistory(activeKeys)

        const results = this.qualifyDetectionResults(barcodes)

        // check again and break out, since onResult may not immediately stop
        // the detector.
        if (!this.running) {
          break
        }

        if (results.length > 0) {
          onResult(results)
        }

        // get remaining headroom after detection to stay near target FPS
        const delayTime = Math.max(0, DETECTION_INTERVAL_MS - (end - start))

        await this.delay(delayTime)
      }
    } catch (error) {
      onError?.(error)
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private qualifyDetectionResults(barcodes: DetectedBarcode[]): BarcodeDetectionResult[] {
    if (barcodes.length === 0) {
      return []
    }

    const primaryIndex = this.findPrimaryBarcodeIndex(barcodes)
    const videoWidth = this.videoElement?.videoWidth || this.videoElement?.clientWidth || 0
    const videoHeight = this.videoElement?.videoHeight || this.videoElement?.clientHeight || 0

    return barcodes.map((barcode, index) => {
      const cornerPoints = polygonToAABB(barcode.cornerPoints)
      return {
        barcode: barcode.rawValue,
        cornerPoints,
        isPrimary: index === primaryIndex,
        isSteady: this.stabilityTracker.computeSteadiness(barcode.rawValue),
        isNear: this.stabilityTracker.computeCloseness(cornerPoints, videoWidth, videoHeight),
      }
    })
  }

  private findPrimaryBarcodeIndex(barcodes: DetectedBarcode[]): number {
    if (!this.videoElement || barcodes.length === 0) {
      return 0
    }

    const videoWidth = this.videoElement.videoWidth || this.videoElement.clientWidth
    const videoHeight = this.videoElement.videoHeight || this.videoElement.clientHeight
    const videoCenterX = videoWidth / 2
    const videoCenterY = videoHeight / 2

    let primaryIndex = -1
    let minDistanceToCenterSquared = Infinity

    for (let i = 0; i < barcodes.length; i++) {
      const barcode = barcodes[i]
      const sumX = barcode.cornerPoints.reduce((sum, point) => sum + point.x, 0)
      const sumY = barcode.cornerPoints.reduce((sum, point) => sum + point.y, 0)
      const centerX = sumX / barcode.cornerPoints.length
      const centerY = sumY / barcode.cornerPoints.length
      const deltaX = centerX - videoCenterX
      const deltaY = centerY - videoCenterY
      const distanceToCenterSquared = deltaX * deltaX + deltaY * deltaY

      if (distanceToCenterSquared < minDistanceToCenterSquared) {
        minDistanceToCenterSquared = distanceToCenterSquared
        primaryIndex = i
      }
    }

    return primaryIndex
  }
}
